import Bind from 'lodash-decorators/bind';
import React, { Component, createRef, RefObject } from 'react';
import { RGBColor } from 'react-color';
import { renderToString } from 'react-dom/server';
import { DraperyModule } from '../../../redux';
import styled, { theme } from '../../../theme';
import { DraperyHeader } from '../../calculator/styles';
import {
  BlankSvg,
  FivePanelsSvg,
  FourPanelsSvg,
  LeftSvg,
  NoValanceNoReturnsWire,
  NoValanceReturnsWire,
  NoValanceWrappedReturns,
  PairSvg,
  poppinsFont,
  RightSvg,
  SingleSvg,
  ThreePanelsSvg,
  ValanceNoReturnsWire,
  ValanceReturnsWire,
} from './bk_svgs';
import Board, { viewBox } from './board/board';
import Toolbar from './toolbar/toolbar';
import { toast } from 'sonner';
import ImageCropDialog, { ImageCropDialogProps } from './cropper/ImageCropDialog';
import { Button } from '../../ui/button';
import { LucideTrash2, LucideUpload } from 'lucide-react';

interface ContainerProps {
  uiWidth: number;
}

const Container = styled.div<ContainerProps>`
  position: relative;
  width: ${(props) => (props.uiWidth ? `${props.uiWidth}px` : `70vh`)};
  height: inherit;
  padding-top: ${(props) =>
    props.uiWidth
      ? `${(viewBox.height * props.uiWidth) / viewBox.width}px`
      : `${(viewBox.height * 70) / viewBox.width}vh`};
  border: 1px solid ${theme.colors.grayRGBA};
  @media screen and (max-width: 992px) {
    width: 100%;
  }
`;

const RatioContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;

// const UploadedImage = styled.img`
//   position: absolute;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
//   object-fit: contain;
//   pointer-events: none;
// `;

const UploadedImageActions = styled.div`
  display: flex;
  gap: 6px;
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  margin-bottom: 10px;
`;

const HelpText = styled.div`
  margin-left: 10px;
  align-self: flex-end;
  padding-bottom: 8px;
`;

interface Props {
  shapes?: DraperyModule.Shape[];
  category: number;
  panelType?: string;
  panelJson: DraperyModule.DraperyOrderDesignPanelJson;
  rodWidth?: number;
  finishedLengthOfPanels: number;
  disabled: boolean;
  valanceForDrawing?: string;
  onShapeChanged(shapes: DraperyModule.Shape[]): void;
  orderType?: string;
  romanShadeJson?: any;
}

interface State {
  tool: 'line' | 'pen' | 'text' | 'selection' | 'rect' | 'circle';
  arrow: boolean;
  lineWeight: number;
  lineType: 'solid' | 'dashed';
  filledColor: RGBColor;
  strokeColor: RGBColor;
  fontSize: number;
  selected: boolean;
  width: number;
  imageUpload: string | undefined;
  imageUploadFileName: string;
  imageSrc: string | undefined;
  imageBlob: Blob | undefined;
  uploadedFile: File | undefined;
}

class Drawing extends Component<Props, State> {
  public state: State = {
    tool: 'line',
    arrow: false,
    lineWeight: 3,
    lineType: 'solid',
    filledColor: { r: 255, g: 255, b: 255, a: 1 },
    strokeColor: { r: 59, g: 151, b: 177, a: 1 },
    fontSize: 18,
    selected: false,
    width: 0,
    imageUpload: undefined,
    imageUploadFileName: '',
    imageSrc: undefined,
    imageBlob: undefined,
    uploadedFile: undefined,
  };

  private boardRef: RefObject<Board> = createRef();
  public uploadImageRef: any = createRef();

  public constructor(props: Props) {
    super(props);
  }

  public componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    setTimeout(() => {
      this.handleResize();
    }, 0);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  @Bind()
  public handleUploadImage() {
    const el = document.getElementById('drawer_image_upload');
    if (!el) return;
    el.click();
  }

  public convertToPng = async (file: File): Promise<{ pngUrl: string; pngFile: File }> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject('Canvas context is not supported.');
          return;
        }

        canvas.width = image.width;
        canvas.height = image.height;

        ctx.drawImage(image, 0, 0);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject('Conversion to PNG failed.');
              return;
            }

            const pngFile = new File([blob], file.name.replace(/\.\w+$/, '.png'));

            const pngUrl = URL.createObjectURL(pngFile);

            resolve({ pngUrl, pngFile });
          },
          'image/png',
          1
        );
      };

      image.onerror = () => {
        reject('Image loading failed.');
      };

      image.src = URL.createObjectURL(file);
    });
  };

  @Bind()
  public async handleUpload(e: any) {
    // 10MB max size
    const MAX_SIZE = 10 * 1024 * 1024;
    const MAX_SIZE_MB = 10;
    const ALLOWED_EXTENSIONS = ['image/png', 'image/webp', 'image/jpeg', 'image/svg+xml'];

    const file = e?.target?.files?.[0];

    if (!file) {
      toast.error('No file has been selected');
      console.error('No file has been selected');
      return;
    }

    if (!ALLOWED_EXTENSIONS.includes(file?.type)) {
      toast.error(`File not suported.`);
      this.resetUploadInput();
      return;
    }

    if (file.size > MAX_SIZE) {
      toast.error(`Image must be smaller than ${MAX_SIZE_MB}MB`);
      console.error(`Image must be smaller than ${MAX_SIZE_MB}MB`);
      return;
    }

    let fileUrl = '';
    let uploadedFile = file;

    if (file.name.endsWith('.svg') || file.name.endsWith('.webp')) {
      try {
        const { pngUrl, pngFile } = await this.convertToPng(file);
        fileUrl = pngUrl;
        uploadedFile = pngFile;
      } catch (error) {
        console.error(error);
      }
    } else {
      fileUrl = URL.createObjectURL(file);
    }

    this.setState((prev) => ({
      ...prev,
      uploadedFile,
      imageUpload: fileUrl,
      imageUploadFileName: uploadedFile.name,
    }));
  }

  resetUploadInput = () => {
    if (this.uploadImageRef?.current) {
      this.uploadImageRef.current.value = '';
    }
  };

  clearUploadedImage = () => {
    this.resetUploadInput();

    this.setState((prev) => ({
      ...prev,
      imageUpload: undefined,
      imageUploadFileName: '',
      imageSrc: undefined,
      imageBlob: undefined,
      uploadedFile: undefined,
    }));
    const filteredShapes = structuredClone(
      (this.props.shapes || []).filter((f: any) => f.type !== 'image')
    );
    this.props.onShapeChanged(filteredShapes);
  };

  cancelImageUpload = () => {
    this.resetUploadInput();

    this.setState((prev) => ({
      ...prev,
      imageUpload: undefined,
      imageUploadFileName: '',
      imageSrc: undefined,
      imageBlob: undefined,
      uploadedFile: undefined,
    }));
  };

  confirmImageUpload = (croppedBlob: Blob | undefined) => {
    const imageBlob = croppedBlob || undefined;

    if (!imageBlob) return;

    // cleanup starog local preview-a
    if (this.state.imageSrc?.startsWith('blob:')) {
      URL.revokeObjectURL(this.state.imageSrc);
    }

    const imageSrc = URL.createObjectURL(imageBlob);

    this.setState((prev) => ({ ...prev, imageUpload: undefined, imageSrc, imageBlob }));
    const shapes = [...(this.props.shapes || [])].filter((shape: any) => !shape?.isBackgroundImage);
    if (imageSrc) {
      const imageShape: any = {
        points: [
          { x: 0, y: 0 },
          { x: viewBox.width, y: viewBox.height },
        ],
        type: 'image',
        imageSrc,
        imageBlob,
        file: this.state.uploadedFile,
        fileName: this.state.imageUploadFileName,
        isBackgroundImage: true,
        arrow: false,
        lineWeight: 0,
        lineType: 'solid',
        text: '',
        filledColor: { r: 255, g: 255, b: 255, a: 0 },
        strokeColor: { r: 255, g: 255, b: 255, a: 0 },
        fontSize: 0,
      };
      shapes.unshift(imageShape);
    }
    this.props.onShapeChanged(shapes);
    this.resetUploadInput();
  };

  public render() {
    const image_crop_dialog_props: ImageCropDialogProps = {
      open: !!this.state.imageUpload,
      imageSrc: this.state.imageUpload,
      uploadedFile: this.state.uploadedFile,
      onCancel: this.cancelImageUpload,
      onConfirm: this.confirmImageUpload,
    };

    return (
      <>
        {this.props.orderType !== 'roman-shade' && (
          <HeaderContainer>
            <DraperyHeader noMarginBottom noMarginTop>
              DRAWING
            </DraperyHeader>
            <HelpText>
              {!this.props.disabled && this.state.tool === 'selection' && !this.state.selected
                ? `Select an object to move.`
                : `Draw Embellishments, if any, on picture below.`}
            </HelpText>
          </HeaderContainer>
        )}

        {this.props.orderType === 'custom-order' ? (
          <div style={{ width: this.state.width ? this.state.width : '70vh' }}>
            <Container uiWidth={this.state.width}>
              <RatioContainer>
                {this.renderBackground()}
                {/* {this.state.imageSrc && (
                  <UploadedImage src={this.state.imageSrc} alt="Uploaded drawing reference" />
                )} */}
                {!this.props.disabled &&
                  (this.state.imageSrc ||
                    this.props.shapes?.some((i: any) => i.type === 'image')) && (
                    <UploadedImageActions>
                      <Button
                        title="Replace image"
                        className="p-0 min-h-[28px] max-h-[28px] min-w-[28px] max-w-[28px] rounded bg-background/70 backdrop-blur-sm border border-border/60 hover:bg-background flex items-center justify-center text-foreground/80 hover:text-foreground transition-colors"
                        type="button"
                        onClick={this.handleUploadImage}
                        variant={'outline'}
                      >
                        <LucideUpload className="lucide lucide-upload h-3.5 w-3.5" />
                      </Button>
                      <Button
                        title="Delete image"
                        className="p-0 min-h-[28px] max-h-[28px] min-w-[28px] max-w-[28px] rounded bg-background/70 backdrop-blur-sm border border-border/60 hover:bg-background flex items-center justify-center text-foreground/80 transition-colors"
                        type="button"
                        onClick={this.clearUploadedImage}
                        variant={'trash-button'}
                      >
                        <LucideTrash2 className="lucide lucide-trash2 h-3.5 w-3.5" />
                      </Button>
                    </UploadedImageActions>
                  )}
                {this.props.shapes && this.props.shapes?.length <= 0 && (
                  <div className="absolute z-[2] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <button
                        data-qa={`drawing-upload-background`}
                        type="button"
                        onClick={this.handleUploadImage}
                      >
                        <LucideUpload className="lucide lucide-upload h-6 w-6" />
                      </button>
                      <span className="text-sm select-none pointer-events-none">
                        Upload an image to draw on, or start drawing
                      </span>
                    </div>
                  </div>
                )}
                <Board
                  ref={this.boardRef}
                  category={this.props.category}
                  panelType={this.props.panelType}
                  panelJson={this.props.panelJson}
                  rodWidth={this.props.rodWidth}
                  finishedLengthOfPanels={this.props.finishedLengthOfPanels}
                  shapes={this.props.shapes}
                  disabled={this.props.disabled}
                  tool={this.state.tool}
                  arrow={this.state.arrow}
                  lineWeight={this.state.lineWeight}
                  lineType={this.state.lineType}
                  filledColor={this.state.filledColor}
                  strokeColor={this.state.strokeColor}
                  fontSize={this.state.fontSize}
                  onShapeChanged={this.props.onShapeChanged}
                  onShapeSelected={this.handleShapeSelected}
                />
              </RatioContainer>
            </Container>
            {!this.props.disabled && (
              <Toolbar
                uiWidth={this.state.width}
                tool={this.state.tool}
                arrow={this.state.arrow}
                lineWeight={this.state.lineWeight}
                lineType={this.state.lineType}
                filledColor={this.state.filledColor}
                strokeColor={this.state.strokeColor}
                fontSize={this.state.fontSize}
                selected={this.state.selected}
                handleToolbar={this.handleToolbar}
                handleLineWeight={this.handleLineWeight}
                handleColor={this.handleColor}
                handleClear={this.handleClear}
                orderType={this.props.orderType}
                handleUploadImage={this.handleUploadImage}
                handleUpload={this.handleUpload}
                uploadImageRef={this.uploadImageRef}
              />
            )}
          </div>
        ) : (
          <>
            <Container uiWidth={this.state.width}>
              <RatioContainer>
                {this.renderBackground()}
                {/* {this.state.imageSrc && (
                  <UploadedImage src={this.state.imageSrc} alt="Uploaded drawing reference" />
                )} */}
                <Board
                  ref={this.boardRef}
                  category={this.props.category}
                  panelType={this.props.panelType}
                  panelJson={this.props.panelJson}
                  rodWidth={this.props.rodWidth}
                  finishedLengthOfPanels={this.props.finishedLengthOfPanels}
                  shapes={this.props.shapes}
                  disabled={this.props.disabled}
                  tool={this.state.tool}
                  arrow={this.state.arrow}
                  lineWeight={this.state.lineWeight}
                  lineType={this.state.lineType}
                  filledColor={this.state.filledColor}
                  strokeColor={this.state.strokeColor}
                  fontSize={this.state.fontSize}
                  onShapeChanged={this.props.onShapeChanged}
                  onShapeSelected={this.handleShapeSelected}
                />
              </RatioContainer>
            </Container>
            {!this.props.disabled && (
              <Toolbar
                uiWidth={this.state.width}
                tool={this.state.tool}
                arrow={this.state.arrow}
                lineWeight={this.state.lineWeight}
                lineType={this.state.lineType}
                filledColor={this.state.filledColor}
                strokeColor={this.state.strokeColor}
                fontSize={this.state.fontSize}
                selected={this.state.selected}
                handleToolbar={this.handleToolbar}
                handleLineWeight={this.handleLineWeight}
                handleColor={this.handleColor}
                handleClear={this.handleClear}
              />
            )}
          </>
        )}

        <ImageCropDialog {...image_crop_dialog_props} />
      </>
    );
  }

  public renderBackground(isPdf?: boolean) {
    const { panelJson } = this.props;
    if (this.props.orderType === 'roman-shade') {
      switch (this.props.valanceForDrawing) {
        case 'valance-no-returns-wire':
          return (
            <ValanceNoReturnsWire
              rodWidth={this.props.rodWidth}
              finishedLengthOfPanels={this.props.finishedLengthOfPanels}
              panelWidth={panelJson.pannel1Width}
              isPdf={isPdf}
              hideBorder
              romanShadeJson={this.props.romanShadeJson}
            />
          );
        case 'valance-returns-wire':
          return (
            <ValanceReturnsWire
              rodWidth={this.props.rodWidth}
              finishedLengthOfPanels={this.props.finishedLengthOfPanels}
              panelWidth={panelJson.pannel1Width}
              isPdf={isPdf}
              hideBorder
              romanShadeJson={this.props.romanShadeJson}
            />
          );
        case 'no-valance-returns-wire':
          return (
            <NoValanceReturnsWire
              category={this.props.category}
              rodWidth={this.props.rodWidth}
              finishedLengthOfPanels={this.props.finishedLengthOfPanels}
              panelWidth={panelJson.pannel1Width}
              isPdf={isPdf}
              hideBorder
              romanShadeJson={this.props.romanShadeJson}
            />
          );
        case 'no-valance-wrapped-returns':
          return (
            <NoValanceWrappedReturns
              category={this.props.category}
              rodWidth={this.props.rodWidth}
              finishedLengthOfPanels={this.props.finishedLengthOfPanels}
              panelWidth={panelJson.pannel1Width}
              isPdf={isPdf}
              hideBorder
              romanShadeJson={this.props.romanShadeJson}
            />
          );
        default:
          return (
            <NoValanceNoReturnsWire
              category={-1}
              rodWidth={this.props.rodWidth}
              finishedLengthOfPanels={this.props.finishedLengthOfPanels}
              panelWidth={panelJson.pannel1Width}
              isPdf={isPdf}
              hideBorder
              romanShadeJson={this.props.romanShadeJson}
            />
          );
      }
    }

    switch (this.props.panelType) {
      case 'center':
        return (
          <SingleSvg
            category={this.props.category}
            rodWidth={this.props.rodWidth}
            finishedLengthOfPanels={this.props.finishedLengthOfPanels}
            panelWidth={panelJson.pannel1Width}
            isPdf={isPdf}
            hideBorder
          />
        );
      case 'left':
        return (
          <LeftSvg
            category={this.props.category}
            rodWidth={this.props.rodWidth}
            finishedLengthOfPanels={this.props.finishedLengthOfPanels}
            panelWidth={panelJson.pannel1Width}
            isPdf={isPdf}
            hideBorder
          />
        );
      case 'right':
        return (
          <RightSvg
            category={this.props.category}
            rodWidth={this.props.rodWidth}
            finishedLengthOfPanels={this.props.finishedLengthOfPanels}
            panelWidth={panelJson.pannel1Width}
            isPdf={isPdf}
            hideBorder
          />
        );
      case 'pair':
        return (
          <PairSvg
            category={this.props.category}
            rodWidth={this.props.rodWidth}
            finishedLengthOfPanels={this.props.finishedLengthOfPanels}
            panelWidthLeft={panelJson.pannel1Width}
            panelWidthRight={panelJson.pannel2Width}
            isPdf={isPdf}
            hideBorder
          />
        );
      case 'three-panels':
        return (
          <ThreePanelsSvg
            category={this.props.category}
            rodWidth={this.props.rodWidth}
            finishedLengthOfPanels={this.props.finishedLengthOfPanels}
            panelWidthLeft={panelJson.pannel1Width}
            panelWidthMiddle={panelJson.pannel2Width}
            panelWidthRight={panelJson.pannel3Width}
            isPdf={isPdf}
            hideBorder
          />
        );
      case 'four-panels':
        return (
          <FourPanelsSvg
            category={this.props.category}
            rodWidth={this.props.rodWidth}
            finishedLengthOfPanels={this.props.finishedLengthOfPanels}
            panelWidth1={panelJson.pannel1Width}
            panelWidth2={panelJson.pannel2Width}
            panelWidth3={panelJson.pannel3Width}
            panelWidth4={panelJson.pannel4Width}
            isPdf={isPdf}
            hideBorder
          />
        );
      case 'five-panels':
        return (
          <FivePanelsSvg
            category={this.props.category}
            rodWidth={this.props.rodWidth}
            finishedLengthOfPanels={this.props.finishedLengthOfPanels}
            panelWidth1={panelJson.pannel1Width}
            panelWidth2={panelJson.pannel2Width}
            panelWidth3={panelJson.pannel3Width}
            panelWidth4={panelJson.pannel4Width}
            panelWidth5={panelJson.pannel5Width}
            isPdf={isPdf}
            hideBorder
          />
        );
      default:
        return <BlankSvg category={-1} hideBorder imageSrc={this.state.imageSrc} />;
    }
  }

  private async drawImageContainOnContext(
    context: CanvasRenderingContext2D,
    url: string,
    x: number,
    y: number,
    width: number,
    height: number
  ) {
    const image = document.createElement('img');
    image.crossOrigin = 'anonymous';
    image.src = url;

    await new Promise<void>((res, rej) => {
      image.addEventListener('load', () => res());
      image.addEventListener('error', () => rej());
    });

    const imageRatio = image.naturalWidth / image.naturalHeight;
    const boxRatio = width / height;

    let drawWidth = width;
    let drawHeight = height;

    if (imageRatio > boxRatio) {
      drawHeight = width / imageRatio;
    } else {
      drawWidth = height * imageRatio;
    }

    const drawX = x + (width - drawWidth) / 2;
    const drawY = y + (height - drawHeight) / 2;

    context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
  }

  private getBackgroundImageSrc(): string | undefined {
    if (this.state.imageSrc) {
      return this.state.imageSrc;
    }

    const backgroundImageShape = (this.props.shapes || []).find(
      (shape: any) => shape?.type === 'image' && shape?.imageSrc
    ) as any;

    return backgroundImageShape?.imageSrc;
  }

  @Bind()
  public async convertToImage(): Promise<Blob | null> {
    if (this.boardRef.current) {
      try {
        const boardUrl = this.boardRef.current.createImageUrl();
        if (boardUrl === null) {
          throw new Error('Unable to get the drawing image url');
        }
        const bkUrl = this.createBkImageUrl();
        // const bkBase64 = this.createBkImageBase64();

        const canvas = document.createElement('canvas');
        canvas.width = viewBox.width * 3;
        canvas.height = viewBox.height * 3;
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Unable to get the canvas context');
        }

        const backgroundImageSrc = this.getBackgroundImageSrc();
        if (backgroundImageSrc) {
          if (this.props.orderType === 'custom-order') {
            await this.drawImageContainOnContext(
              context,
              backgroundImageSrc,
              0,
              0,
              canvas.width,
              canvas.height
            );
          } else {
            await this.drawImageOnContext(
              context,
              backgroundImageSrc,
              canvas.width,
              canvas.height
            );
          }
        }
        await this.drawImageOnContext(context, bkUrl, canvas.width, canvas.height);
        await this.drawImageOnContext(context, boardUrl, canvas.width, canvas.height);

        // URL.revokeObjectURL(bkUrl);
        // URL.revokeObjectURL(boardUrl);

        // convert to image
        const convertedBlob = await new Promise<Blob>((res, rej) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                rej(new Error('Unable to crop the image'));
              } else {
                res(blob);
              }
            },
            'image/png',
            1
          );
        });

        return convertedBlob;
      } catch (e) {
        return null;
      }
    } else {
      return null;
    }
  }

  @Bind()
  public handleLineWeight(lineWeight: number, lineType: 'solid' | 'dashed') {
    if (this.boardRef.current) {
      this.boardRef.current.handleLineWeightForSelection(lineWeight, lineType);
    }
    this.setState({ lineWeight, lineType });
  }

  @Bind()
  public handleColor(filledColor: RGBColor, strokeColor: RGBColor) {
    if (this.boardRef.current) {
      this.boardRef.current.handleColorForSelection(filledColor, strokeColor);
    }
    this.setState({ filledColor, strokeColor });
  }

  @Bind()
  public handleClear() {
    if (this.state.selected) {
      return;
    }

    // Clear image from board if uploaded
    if (this.state.imageUpload || this.state.imageSrc) {
      this.resetUploadInput();

      this.setState((prev) => ({
        ...prev,
        uploadedFile: undefined,
        imageUpload: undefined,
        imageSrc: undefined,
      }));
    }

    if (this.boardRef.current) {
      this.boardRef.current.handleClear();
    }
  }

  @Bind()
  private handleToolbar(type: 'line' | 'pen' | 'arrow' | 'text' | 'selection' | 'rect' | 'circle') {
    if (this.state.selected) {
      return;
    }
    if (type === 'line') {
      this.setState({ tool: 'line', arrow: false });
    } else if (type === 'arrow') {
      this.setState({ tool: 'line', arrow: true });
    } else {
      this.setState({ tool: type, arrow: false });
    }
  }

  @Bind()
  private handleShapeSelected(selected: boolean) {
    this.setState({ selected });
  }

  @Bind()
  private createBkImageUrl(): string {
    const bkSvg = renderToString(this.renderBackground(true)).replace(
      '<defs></defs>',
      poppinsFont()
    );
    const bkBlob = new Blob([bkSvg], { type: 'image/svg+xml' });
    const bkUrl = URL.createObjectURL(bkBlob);
    return bkUrl;
  }

  // @Bind()
  // private createBkImageBase64(): string {
  //     const bkSvg = renderToString(this.renderBackground(true));
  //     const base64 = "data:image/svg+xml;base64," + window.btoa(bkSvg);
  //     return base64;
  // }

  private async drawImageOnContext(
    context: CanvasRenderingContext2D,
    url: string,
    width: number,
    height: number,
    x: number = 0,
    y: number = 0
  ) {
    const image = document.createElement('img');
    image.src = url;
    await new Promise<void>((res) => {
      image.addEventListener('load', () => {
        res();
      });
    });
    context.drawImage(image, x, y, width, height);
    // URL.revokeObjectURL(url);
  }

  private handleResize = () => {
    const e = document.getElementById('react-tabs-1');
    if (!e) {
      // If the element is not available yet, try again in the next frame
      requestAnimationFrame(this.handleResize);
    } else {
      const width = Math.min(e.offsetWidth - 20, e.offsetHeight);
      if (width < 1) {
        return;
      } else if (width !== this.state.width) {
        this.setState({ width });
      }
    }
  };
}

export default Drawing;
